import { toPathString, UseFormReturn, ValidateFn } from "@/lib/form/form"
import { LimitValidationError } from "@/lib/validators/limit"
import {
  MarketType,
  ParamFormData,
  ProductSectionFormData,
} from "@/packaging/lib/form"
import { toPath } from "lodash"
import * as y from "yup"

export const leakageValiSchema = y
  .object({
    id: y.string().required(),
    value: y
      .array(
        y.object({
          id: y.string().required(),
          count: y.number().required(),
          type: y.string().required(),
        })
      )
      .required(),
    recordedAt: y.string(),
  })
  .test({
    name: "test-leakage-parameter",
    test(value, ctx) {
      const context = ctx.options.context as
        | LeakageValidationContext
        | undefined

      if (!context) {
        return ctx.createError({
          message: `${ctx.path} validation context not specified`,
        })
      }

      if (!context.depends) {
        return ctx.createError({
          message: `${context.path} depends is not specified`,
        })
      }

      if (!context.form) {
        return ctx.createError({
          message: `${context.path} form is not provided`,
        })
      }

      const dependsValue = context.form.getValues(context.depends)
      if (!dependsValue?.value) {
        return true
      }

      const total = Number(dependsValue?.value)
      if (total === 0) {
        return true
      }

      if (context.required) {
        if (!value.value || !value.value.length) {
          return ctx.createError({
            message: `${context.path} leakage is required`,
          })
        }
      }

      const leakedCount =
        value.value?.reduce((acc, curr) => {
          return acc + curr.count
        }, 0) || 0

      if (leakedCount !== total) {
        return ctx.createError({
          message: `${context.path} leakages count not matching total leaked count`,
        })
      }

      return true
    },
  })

export interface LeakageValidationContext {
  path: string
  depends: string
  required?: boolean
  form?: UseFormReturn
}

export interface CreateLeakageValidatorParams {
  path: string
  depends: string
  required?: boolean
}
export function createLeakageValidator(
  params: CreateLeakageValidatorParams
): ValidateFn {
  const finalSchema = params.required
    ? leakageValiSchema.required()
    : leakageValiSchema

  return (value, form) => {
    try {
      finalSchema.validateSync(value, {
        context: {
          form: form,
          path: params.path,
          depends: params.depends,
          required: params.required,
        } satisfies LeakageValidationContext,
      })

      return false
    } catch (err) {
      return err as y.ValidationError
    }
  }
}

export interface CreateLeakedPacketsValidatorParams {
  path: string
  depends: string
  required?: boolean
}

const leakedPacketsValiSchema = y
  .object({
    id: y.string().required(),
    value: y.number().required(),
    remark: y.string().optional(),
    recordedAt: y.string().required(),
  })
  .test({
    name: "test-leaked-packets",
    test(value, ctx) {
      const context = ctx.options.context as
        | LeakedPacketsValidationContext
        | undefined

      if (!context) {
        return ctx.createError({ message: `${ctx.path} context not given` })
      }

      const form = context.form
      const sectionPath = toPathString(toPath(context.path).slice(0, -2))
      const totalPacketsField = form.getValues(
        `${sectionPath}.parameters[0]`
      ) as ParamFormData | undefined

      if (!totalPacketsField || !value.value) {
        return true
      }

      const totalPackets = Number(totalPacketsField.value)
      const leakage = (value.value / totalPackets) * 100

      if (!(leakage >= 0 && leakage <= 0.25)) {
        if (value.remark) {
          return true
        }

        return ctx.createError({
          message: `${context.path} ${LimitValidationError.OutOfRange}`,
        })
      }

      return true
    },
  })

interface LeakedPacketsValidationContext {
  form: UseFormReturn
  path: string
  depends: string
  required?: boolean
}

export function createLeakedPacketsValidator(
  params: CreateLeakedPacketsValidatorParams
): ValidateFn {
  const finalSchema = params.required
    ? leakedPacketsValiSchema.required()
    : leakedPacketsValiSchema

  return (value, form) => {
    try {
      finalSchema.validateSync(value, {
        context: {
          form: form,
          path: params.path,
          depends: params.depends,
          required: params.required,
        } satisfies LeakageValidationContext,
      })

      return false
    } catch (err) {
      return err as y.ValidationError
    }
  }
}

export interface CreateGeneralSectionValidatorParams {
  path: string
}

export function createGeneralSectionValidator(
  params: CreateGeneralSectionValidatorParams
): ValidateFn {
  return (value) => {
    if (!value) {
      return new y.ValidationError(`${params.path} general section is required`)
    }

    const section = value as ProductSectionFormData

    const market = section.parameters.at(0)?.value
    if (!market) {
      return new y.ValidationError(`${params.path} market is required`)
    }

    const product = section.parameters.at(1)?.value
    const sku = section.parameters.at(2)?.value
    const exportTo = section.parameters.at(3)?.value
    const poNumber = section.parameters.at(4)?.value

    if (!(sku && product)) {
      return new y.ValidationError(`${params.path} product and sku is required`)
    }

    if (market === MarketType.Export) {
      if (exportTo && poNumber) {
        return false
      }

      return new y.ValidationError(
        `${params.path} exportTo and poNumber is required`
      )
    }

    return false
  }
}
